import { useState } from "react";
import { useTranslation } from "react-i18next";
import { selfUnassign, type Program, type SelfUnassign } from "@heraion/shared";

import {
  isReadOnlyItem,
  type LibraryItem,
  type LibraryState,
} from "./useLibrary";

// The Library's guarded management writes (HR-S4), extracted from
// LibraryBrowser so that component stays layout + grouping. ONE shape for
// every write: clear the stale error, mark the item busy, surface a
// localized message on failure (the raw api_error_NNN code stays a console
// breadcrumb, never UI copy). Follow keeps its own busy key because the
// card swaps its label to "Following…"; the other writes share one.
export function useLibraryActions(
  lib: Pick<
    LibraryState,
    // CO-34: the assignees writes and the two identity fields join the
    // Pick because "Elimina" is no longer one write: which one it is
    // depends on WHO is asking (`callerSub`) and from WHICH view
    // (`authoredAs`), both of which `useLibrary` already resolves.
    | "setProgramStatus"
    | "deleteItem"
    | "setProgramAssignees"
    | "setPlanAssignees"
    | "callerSub"
    | "authoredAs"
    // The follow set and its one write: on a followed row the press is
    // neither a delete nor a leave, it is "stop following" (below).
    | "followedKeys"
    | "unfollow"
  >,
  /** Host delete-confirm seam (mobile Alert); omitted → the card two-taps.
   *  CO-34. The third argument is the gesture's real nature: non-null when
   *  the press removes the caller from `assignedTo` instead of destroying
   *  the row, so the prompt can say so. A host written before this (or one
   *  that doesn't care) simply takes two arguments and still type-checks. */
  confirmDelete?: (
    item: LibraryItem,
    run: () => void,
    leaving?: SelfUnassign | null,
    /** True when the row is FOLLOWED, so the press deletes the caller's
     *  pointer and the author keeps the program. A fourth optional
     *  parameter for the same reason `leaving` was a third: a host that
     *  ignores it keeps its old signature and still type-checks. */
    unfollowing?: boolean,
  ) => void,
) {
  const { t } = useTranslation();
  const [actionError, setActionError] = useState<string | null>(null);
  const [followingKey, setFollowingKey] = useState<string | null>(null);
  const [mutatingKey, setMutatingKey] = useState<string | null>(null);

  const runMutation = async (
    item: LibraryItem,
    setBusy: (k: string | null) => void,
    fn: () => Promise<Error | null>,
    tag: string,
    errKey: string,
    errDefault: string,
  ) => {
    setActionError(null);
    setBusy(item.key);
    const err = await fn();
    setBusy(null);
    if (err) {
      console.warn(`[library] ${tag}`, err.message);
      setActionError(t(errKey, { defaultValue: errDefault }));
    }
  };

  const onFollow = (item: LibraryItem) => {
    if (item.source !== "personal" || item.kind !== "program") return;
    if (followingKey) return;
    void runMutation(
      item,
      setFollowingKey,
      () => lib.setProgramStatus(item.program, "active"),
      "follow_failed",
      "library.errFollow",
      "Could not follow the program.",
    );
  };

  const onSetStatus = (item: LibraryItem, status: Program["status"]) => {
    if (item.source !== "personal" || item.kind !== "program") return;
    if (mutatingKey) return;
    void runMutation(
      item,
      setMutatingKey,
      () => lib.setProgramStatus(item.program, status),
      "status_failed",
      "library.errStatus",
      "Could not update the program.",
    );
  };

  // CO-34: is this press a delete, or the author stepping out of their own
  // shared row? One computation, read by BOTH the confirm copy and the write
  // below, so a prompt that promises "you are only removing it from your
  // library" can never be followed by a DELETE. Templates are nobody's row,
  // hence never this. See shared/selfUnassign for every `null` branch.
  const leavingInstead = (item: LibraryItem): SelfUnassign | null =>
    item.source !== "personal"
      ? null
      : selfUnassign(
          item.kind === "program" ? item.program : item.plan,
          lib.callerSub,
          lib.authoredAs,
        );

  // A followed row: the caller holds a FOLLOW# pointer, not the program.
  // ⛔ The THIRD branch, next to delete and leave, never inside either
  // (next/coaching.md, "Smetti di seguire"): `selfUnassign` returns null
  // here, since it only covers the author stepping out of his own row, and
  // `deleteItem` would DELETE a program that is not the caller's.
  const unfollowingInstead = (item: LibraryItem): boolean =>
    isReadOnlyItem(item, lib.followedKeys);

  const runDeleteOrLeave = (
    item: LibraryItem,
    leaving: SelfUnassign | null,
  ): Promise<Error | null> => {
    if (item.source === "personal" && unfollowingInstead(item))
      return lib.unfollow(
        item.kind,
        item.kind === "program" ? item.program.id : item.plan.id,
      );
    if (leaving === null || item.source !== "personal")
      return lib.deleteItem(item);
    // `assigneeEmails` is deliberately omitted: it is the "invite by email"
    // half of the patch, and this gesture resolves no new address. The
    // server withdraws the outstanding proposals on its own
    // (withdrawAssignmentProposals, programsLifecycle.ts).
    const body = { assignedTo: leaving.assignedTo };
    return item.kind === "program"
      ? lib.setProgramAssignees(item.program, body)
      : lib.setPlanAssignees(item.plan, body);
  };

  const onDeleteItem = (item: LibraryItem) => {
    if (mutatingKey) return;
    const leaving = leavingInstead(item);
    const unfollowing = unfollowingInstead(item);
    void runMutation(
      item,
      setMutatingKey,
      () => runDeleteOrLeave(item, leaving),
      unfollowing
        ? "unfollow_failed"
        : leaving === null
          ? "delete_failed"
          : "leave_failed",
      unfollowing
        ? "library.errUnfollow"
        : leaving === null
          ? "library.errDelete"
          : "library.errLeave",
      unfollowing
        ? "Could not stop following. Try again."
        : leaving === null
          ? "Could not delete. Try again."
          : "Could not remove it from your library. Try again.",
    );
  };

  const requestDelete = (item: LibraryItem) => {
    if (confirmDelete) {
      // Recomputed rather than threaded from a render: the press is the
      // moment that matters, and `assignedTo` may have changed since.
      confirmDelete(
        item,
        () => onDeleteItem(item),
        leavingInstead(item),
        unfollowingInstead(item),
      );
      return;
    }
    onDeleteItem(item);
  };

  return {
    actionError,
    setActionError,
    followingKey,
    mutatingKey,
    onFollow,
    onSetStatus,
    requestDelete,
  };
}
